import React from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, UserPlus, Crown, MoreVertical, UserMinus, AlertTriangle, Wallet, Pencil } from "lucide-react";
import { UserAvatar } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtMoney } from "@/lib/constants";

export function AdminsPanel({ data, onInvite, onDemote }) {
  const { t } = useTranslation();
  const admins = data.admins || [];
  const lastAdmin = (data.admin_count || 0) <= 1;
  return (
    <div className="mb-6 rounded-[0.7rem] border border-border bg-card p-5" data-testid="admins-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><ShieldCheck size={18} strokeWidth={1.75} /></span>
          <div>
            <h2 className="text-base font-semibold tracking-tight">{t("hr.admins.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("hr.admins.subtitle")}</p>
          </div>
        </div>
        <Button data-testid="open-invite-admin" size="sm" variant="outline" onClick={onInvite} className="rounded-lg gap-1.5 bg-card shrink-0">
          <UserPlus size={15} /> {t("hr.admins.inviteBtn")}
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {admins.map((a) => (
          <div key={a.user_id} data-testid={`admin-row-${a.user_id}`} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2.5">
            <UserAvatar name={a.name} src={a.picture} size={9} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate flex items-center gap-1.5">
                {a.name}
                {a.is_owner && <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-primary"><Crown size={11} /> {t("hr.admins.owner")}</span>}
                {a.is_self && !a.is_owner && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("hr.admins.you")}</span>}
              </p>
              <p className="text-xs text-muted-foreground truncate">{a.email}</p>
            </div>
            {!a.is_owner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`admin-actions-${a.user_id}`} className="grid place-items-center w-8 h-8 rounded-md text-muted-foreground hover:bg-secondary transition-colors shrink-0"><MoreVertical size={16} /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid={`admin-demote-${a.user_id}`} disabled={lastAdmin} onClick={() => onDemote(a)} className="text-destructive focus:text-destructive">
                    <UserMinus size={15} className="mr-2" /> {t("hr.admins.changeToMember")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BudgetSummary({ budget }) {
  const { t } = useTranslation();
  const cur = budget.currency || "EUR";
  const total = budget.total || 0;
  const usedPct = total > 0 ? Math.round((budget.used / total) * 100) : 0;
  const overThreshold = total > 0 && usedPct >= (budget.alert_threshold_pct || 0);
  const overAllocated = (budget.allocated_total || 0) > total;
  const cards = [
    { label: t("hr.employees.summaryTotal"), value: fmtMoney(total, cur) },
    { label: t("hr.employees.summaryAllocated"), value: fmtMoney(budget.allocated_total, cur), warn: overAllocated },
    { label: t("hr.employees.summaryUsed"), value: fmtMoney(budget.used, cur), sub: total > 0 ? `${usedPct}%` : null },
    { label: t("hr.employees.summaryRemaining"), value: fmtMoney(Math.max(0, total - budget.used), cur), good: true },
  ];
  return (
    <div className="mb-6 space-y-3" data-testid="budget-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-[0.7rem] border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.label}</p>
            <p className={`text-lg font-semibold tabular mt-1 ${c.good ? "text-success" : c.warn ? "text-destructive" : "text-foreground"}`}>{c.value}</p>
            {c.sub && <p className="text-[11px] text-muted-foreground mt-0.5">{c.sub} {t("rates.ofBudget")}</p>}
          </div>
        ))}
      </div>
      {overThreshold && (
        <div data-testid="budget-alert" className="flex items-start gap-3 rounded-[0.7rem] border border-warning/40 bg-warning/5 p-4">
          <AlertTriangle size={18} className="text-warning shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">{t("rates.alertBannerTitle")}</span> — {t("rates.alertBannerBody", { pct: usedPct, threshold: budget.alert_threshold_pct })}</p>
        </div>
      )}
      {overAllocated && (
        <div className="flex items-start gap-3 rounded-[0.7rem] border border-destructive/30 bg-destructive/5 p-4">
          <AlertTriangle size={18} className="text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">{t("hr.employees.overAllocated")}</p>
        </div>
      )}
    </div>
  );
}

export function BudgetCell({ e, currency, onEdit, label }) {
  const allocated = e.budget_allocated || 0;
  const used = e.budget_used || 0;
  const pct = allocated > 0 ? Math.min(100, Math.round((used / allocated) * 100)) : 0;
  const over = allocated > 0 && used > allocated;
  if (!allocated) {
    return (
      <button data-testid={`budget-cell-${e.user_id}`} onClick={onEdit} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
        <Wallet size={13} /> {label}
      </button>
    );
  }
  return (
    <button data-testid={`budget-cell-${e.user_id}`} onClick={onEdit} className="group w-full max-w-[13rem] text-left">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="tabular text-foreground font-medium">{fmtMoney(used, currency)} <span className="text-muted-foreground font-normal">/ {fmtMoney(allocated, currency)}</span></span>
        <Pencil size={12} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${over ? "bg-destructive" : "bg-primary"}`} style={{ width: `${Math.max(4, pct)}%` }} />
      </div>
    </button>
  );
}
